/**
 * EXPERT SERVICES (EXPERT-ROOMS.md §5, E8 X3) — what the experts serving this room offer (each with the department
 * room that fulfils it), and its FORM: open (pre-filled from your draft or what the experts already know), save a draft,
 * check every field, and SEND it — a request to the department room that fulfils it (X4). Additive section.
 */
import React, { useState } from 'react';
import FormCard from '@stepflow/ui-shared/components/forms/FormCard.jsx';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';

export default function RoomExpertServices({ value, onOffering, onSent, initial = null }) {
  const experts = Array.isArray(value) ? value : [];
  const [open, setOpen] = useState(null);       // {expertNetworkId, offering, values, errors, complete?, note?}
  // a room mention ("@Platform …") opens the Team's form directly, with the message as the request's note
  const [seeded, setSeeded] = useState(null);
  const [teamFilter, setTeamFilter] = useState(null);   // {expertNetworkId, team, note} — a mention of a Team with several offerings
  React.useEffect(() => {
    if (initial && initial.nonce !== seeded && initial.expertNetworkId && !initial.offeringId) {
      setSeeded(initial.nonce);
      setOpen(null);
      setTeamFilter({ expertNetworkId: initial.expertNetworkId, team: initial.team || '', note: initial.note || '' });
    }
    if (initial && initial.nonce !== seeded && initial.expertNetworkId && initial.offeringId) {
      setSeeded(initial.nonce);
      (async () => {
        const r = await onOffering({ action: 'open', expertNetworkId: initial.expertNetworkId, offeringId: initial.offeringId });
        if (r) setOpen({ expertNetworkId: initial.expertNetworkId, offering: r.offering, values: r.values || {}, errors: {},
          note: initial.note || '' });
      })().catch(() => {});
    }
  }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const call = async (req) => {
    setBusy(true); setMsg('');
    try {
      const r = await onOffering(req);
      return r;
    } catch (e) {
      setMsg(e?.message || "Couldn't do that.");
      return null;
    } finally {
      setBusy(false);
    }
  };
  const openForm = async (eid, oid) => {
    const r = await call({ action: 'open', expertNetworkId: eid, offeringId: oid });
    if (r) setOpen({ expertNetworkId: eid, offering: r.offering, values: r.values || {}, errors: {},
      note: (teamFilter && teamFilter.expertNetworkId === eid) ? teamFilter.note : '' });
    setTeamFilter(null);
  };
  const act = async (action) => {
    const r = await call({ action, expertNetworkId: open.expertNetworkId, offeringId: open.offering.id, values: open.values,
      ...(action === 'submit' ? { note: open.note || '' } : {}) });
    if (!r) return;
    if (action === 'submit' && r.request) {
      setOpen(null);
      setMsg(`Sent to ${r.request.team || 'the team'} — follow it under Requests.`);
      onSent?.(r.request);
      return;
    }
    setOpen({ ...open, errors: r.errors || {}, complete: !!r.complete });
    if (action === 'save' && !Object.keys(r.errors || {}).length) setMsg('Draft saved.');
    if (action === 'check' && r.complete) setMsg('Everything the team needs is here.');
  };
  if (open) {
    return (
      <div className="space-y-2 text-[12px]" data-testid="expert-service-form">
        <p className="text-[12px] font-medium text-gray-900">{open.offering.title}</p>
        {open.offering.summary ? <p className="text-[11px] text-gray-500">{open.offering.summary}</p> : null}
        <FormCard form={open.offering.form} values={open.values} errors={open.errors} disabled={busy}
          onChange={(values) => setOpen({ ...open, values, complete: false })} />
        <label className="block text-[11px] font-medium text-gray-700">A note for the team (optional)
          <textarea rows={2} value={open.note || ''} disabled={busy} data-testid="expert-service-note"
            onChange={(e) => setOpen({ ...open, note: e.target.value })}
            className="mt-0.5 block w-full rounded-md border border-gray-300 px-2 py-1 text-xs" />
        </label>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={busy} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}
            onClick={() => act('submit')} data-testid="expert-service-submit">Send to {open.offering.team || 'the team'}</button>
          <button type="button" disabled={busy} className={`${btn} text-gray-700 hover:bg-gray-100`}
            onClick={() => act('save')} data-testid="expert-service-save">Save draft</button>
          <button type="button" disabled={busy} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`}
            onClick={() => act('check')} data-testid="expert-service-check">Check</button>
          <button type="button" disabled={busy} className={`${btn} text-gray-500 hover:bg-gray-100`}
            onClick={() => { setOpen(null); setMsg(''); }}>Back</button>
        </div>
        {msg ? <p className="text-[11px] text-gray-600" data-testid="expert-service-msg">{msg}</p> : null}
      </div>
    );
  }
  return (
    <div className="space-y-2 text-[12px]" data-testid="room-expert-services">
      {teamFilter ? (
        <p className="text-[11px] text-gray-600" data-testid="expert-services-team-filter">
          What the {teamFilter.team} team offers — pick one; your message becomes its note.
          {' '}<button type="button" className="underline" onClick={() => setTeamFilter(null)}>Show everything</button>
        </p>
      ) : null}
      {experts.filter((x) => !teamFilter || x.expertNetworkId === teamFilter.expertNetworkId).map((x) => (
        <div key={x.expertNetworkId}>
          <p className="text-[11px] font-medium text-gray-900">{x.name}{x.own ? ' (this team — preview)' : ''}</p>
          {(x.offerings || []).filter((o) => !teamFilter
            || String(o.team || '').toLowerCase() === String(teamFilter.team).toLowerCase()).map((o) => (
            <div key={o.id} className="mt-1 rounded-md border border-gray-200 px-2 py-1.5" data-testid={`expert-offering-${o.id}`}>
              <p className="text-[11px] font-medium text-gray-800">{o.title}</p>
              {o.summary ? <p className="text-[11px] text-gray-500">{o.summary}</p> : null}
              <p className="text-[10px] text-gray-500">
                {o.team ? `By ${o.team}${o.teamRoom ? ` (${o.teamRoom})` : ''}` : ''}
                {o.approval?.when === 'always' ? ' · needs approval' : ''}
              </p>
              <button type="button" disabled={busy} className={`${btn} mt-1 text-indigo-700 hover:bg-indigo-50`}
                onClick={() => openForm(x.expertNetworkId, o.id)} data-testid={`expert-offering-open-${o.id}`}>Open form</button>
            </div>
          ))}
        </div>
      ))}
      {msg ? <p className="text-[11px] text-gray-700" data-testid="expert-service-msg">{msg}</p> : null}
    </div>
  );
}
